/// <amd-module name="Repos/Changes/Components/ChangeCompareHeader" />
import * as React from "react";
import * as Resources from "Repos/Changes/Resources";
import { useChangesContext } from "Repos/ChangesCommon/ChangesContext";
import {
    DiffViewerType,
    IChange,
    IChangeCollection,
    IChangeItemContent,
    IChangesFilesService,
    IChangesNavigationService,
    IChangesViewService,
    IDiffNavigation
} from "Repos/ChangesCommon/Contracts";
import { IEditorUserPreferencesService } from "Repos/Common/Contracts/Editor";
import { VersionControlChangeType } from "Repos/Common/Contracts/Git";
import { DiffViewerOrientation } from "Repos/Common/Contracts/VersionControlViewModel";
import { summarizeChangeTypes } from "Repos/Common/Util/ChangeCounts";
import { IDiscussionIntegrationService } from "Repos/Discussion/Common/DiscussionContracts";
import { getContentPreviewService } from "Repos/Files/ContentPreview";
import { IReadonlyObservableValue } from "VSS/Core/Observable";
import { ScreenContext, ScreenSize } from "VSS/Core/Util/Screen";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { Button } from "VSSUI/Button";
import { Filter, IFilterProps } from "VSSUI/Filter";
import { HeaderCommandBar, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IMenuItem } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { SplitButton } from "VSSUI/SplitButton";
import { Tooltip } from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";
import { ChangedFileViewMode } from "../Contracts";
import { getChangesForPath } from "../Util";
import { IIndexingToolTipProvider, IContributedLanguageProvider, NotInitializedButton } from "Repos/Views/FilesHub/Components/FilesHeader";

interface ICompareHeaderProps<TVersionDescriptor> {
    changes: IChangeCollection<TVersionDescriptor>;
    discussionService?: IDiscussionIntegrationService;
    filterProps?: IFilterProps;
    fileViewMode: IReadonlyObservableValue<ChangedFileViewMode>;
    onNavButtonClicked?: () => void;
    pageContext: IVssPageContext;
    preFilterContent?: JSX.Element;
    setFileViewMode: (viewMode: ChangedFileViewMode) => void;
}

export function ChangeCompareHeader<TVersionDescriptor>(props: ICompareHeaderProps<TVersionDescriptor>): JSX.Element {
    const { discussionService, filterProps, onNavButtonClicked, preFilterContent } = props;

    const { filesService, navigationService, viewService } = useChangesContext<TVersionDescriptor>();

    const selectedItem = navigationService.getSelectedItem().value;

    const fileIsRoot = selectedItem.path === "/";
    const changesForPath = getChangesForPath(props.changes, selectedItem.path);
    const changeCountSummary = summarizeChangeTypes(changesForPath.map(c => c.changeType));
    const numChanges = changesForPath.length;

    const [indexingContribution, setIndexingContribution] = React.useState<IContributedLanguageProvider>();
    const [indexingButtonProperties, setIndexingButtonProperties] = React.useState<IIndexingToolTipProvider>();
    const [currentFile, setCurrentFile] = React.useState<string>();

    // Checks if the current file is indexed
    const checkIfAFileIsIndexed = React.useCallback(async (item: IChange) => {
        if (item.name !== currentFile) {
            const indexingToolTipResponse = await indexingContribution?.getToolTipProvider!(item.path.substring(1), selectedItem.commitId, true);
            if (indexingToolTipResponse) {
                setIndexingButtonProperties({
                    displayToolTip: indexingToolTipResponse.displayToolTip,
                    buttonText: indexingToolTipResponse.buttonText,
                    buttonColor: indexingToolTipResponse.buttonColor,
                    buttonIcon: indexingToolTipResponse.buttonIcon,
                    onClickRef: indexingToolTipResponse.onClickRef
                });
                setCurrentFile(item.name);
            }
        }
    }, [indexingContribution, indexingButtonProperties, currentFile]);

    React.useEffect(() => {
        if (!indexingContribution) {
            // Gets indexing contribution type
            const getContributionsByType = async () => {
                const indexingContributionType = "ms.vss-features.code-editor-navigation-provider";
                const contributionService = props.pageContext.getService<IVssContributionService>("IVssContributionService");
                const providerContributions = contributionService.getContributionsByType(indexingContributionType);
                for (const providerContributionId in providerContributions) {
                    var indexingContributionResponse: IContributedLanguageProvider | undefined = await contributionService.getService(providerContributionId);
                    setIndexingContribution({ availableLanguages: indexingContributionResponse?.availableLanguages!, getToolTipProvider: indexingContributionResponse?.getToolTipProvider });
                }
            };

            getContributionsByType();
        }
    }, []);

    return (
        <div className="repos-compare-toolbar flex-row flex-center">
            <ScreenContext.Consumer>
                {screen => (
                    <Observer
                        diffNavigation={navigationService.getDiffNavigation()}
                        diffOrientation={filesService.getDiffOrientation(selectedItem.isFolder ? DiffViewerType.summary : DiffViewerType.detail)}
                        fileViewMode={props.fileViewMode}
                        screenSize={screen.size}
                    >
                        {(contentProps: {
                            diffNavigation: IDiffNavigation | undefined;
                            fileViewMode: ChangedFileViewMode;
                            screenSize: ScreenSize;
                        }) => {
                            const smallScreen = contentProps.screenSize <= ScreenSize.small;
                            const xsmallScreen = contentProps.screenSize <= ScreenSize.xsmall;
                            const isEdit =
                                (selectedItem.changeType & VersionControlChangeType.Edit) !== 0 &&
                                (selectedItem.changeType & VersionControlChangeType.Add) === 0;
                            const isDiffEditor = isEdit && contentProps.fileViewMode === ChangedFileViewMode.Diff;
                            const headerItemProps: IChangeCompareHeaderItemProps = {
                                fileViewMode: contentProps.fileViewMode,
                                selectedItem: selectedItem,
                                pageContext: props.pageContext,
                                setFileViewMode: props.setFileViewMode
                            };

                            const fileContent = selectedItem.isFolder
                                ? undefined
                                : filesService.getItemContent(selectedItem.path, filesService.convertVersionToDescriptor(selectedItem.commitId));

                            const languageByExt: { [key: string]: string } = {
                                "cpp": "cpp",
                                "cs": "csharp",
                                "vb": "csharp",
                                "h": "cpp",
                                "java": "java",
                                "js": "javascript",
                                "jsx": "javascript",
                                "ts": "typescript",
                                "tsx": "typescript",
                            };

                            // Gets the file extension
                            const getFileExtensions = (selectedItem: IChange): string => {
                                return selectedItem.name.split('.').slice(-1)[0];
                            }

                            // Determines if the indexing should be displayed
                            const checkIfIndexingExtensionIsAvailable = (selectedItem: IChange): boolean => {
                                if (indexingContribution && Object.keys(indexingContribution).length > 0) {
                                    const fileExtension = getFileExtensions(selectedItem);
                                    if (fileExtension) {
                                        return indexingContribution.availableLanguages.indexOf(languageByExt[fileExtension]) != -1;
                                    }
                                }
                                return false;
                            }

                            if (checkIfIndexingExtensionIsAvailable(selectedItem) && indexingContribution) {
                                checkIfAFileIsIndexed(selectedItem);
                            }

                            return (
                                <div
                                    className={css(
                                        "flex-grow scroll-hidden rhythm-horizontal-8",
                                        xsmallScreen ? "flex-column" : "flex-row flex-center"
                                    )}
                                >
                                    <div className="flex-row flex-noshrink flex-center scroll-hidden rhythm-horizontal-8">
                                        {preFilterContent}
                                        {filterProps && <Filter {...filterProps} className={css("repos-compare-filter", filterProps.className)} />}
                                        {smallScreen && !selectedItem.isFolder && (
                                            <Button
                                                onClick={() => showPreferences(props.pageContext, isDiffEditor)}
                                                iconProps={{ iconName: "Equalizer" }}
                                                subtle={true}
                                            />
                                        )}
                                    </div>
                                    <div className="flex-row flex-grow scroll-hidden rhythm-horizontal-8 flex-center margin-vertical-8">
                                        {xsmallScreen && onNavButtonClicked && (
                                            <Button iconProps={{ iconName: "FolderList" }} onClick={onNavButtonClicked} subtle={true} />
                                        )}
                                        <div className="flex-column scroll-hidden margin-left-16">
                                            <span className="flex-row rhythm-horizontal-8">
                                                <Tooltip
                                                    text={selectedItem.isFolder ? changeCountSummary : selectedItem.name}
                                                    overflowOnly={!selectedItem.isFolder}
                                                >
                                                    <span className="body-m text-ellipsis" role="heading" aria-level={2}>
                                                        {selectedItem.isFolder && numChanges
                                                            ? format(numChanges > 1 ? Resources.ChangedFiles : Resources.ChangedFile, numChanges)
                                                            : selectedItem.name}
                                                    </span>
                                                </Tooltip>
                                                {contentProps.diffNavigation && contentProps.diffNavigation.totalRemovedLines > 0 && (
                                                    <span className="repos-compare-removed-lines font-weight-semibold">
                                                        <Tooltip
                                                            text={format(
                                                                Resources.LinesRemovedTooltip,
                                                                contentProps.diffNavigation.totalRemovedLines
                                                            )}
                                                        >
                                                            <span>-{contentProps.diffNavigation.totalRemovedLines}</span>
                                                        </Tooltip>
                                                    </span>
                                                )}
                                                {contentProps.diffNavigation && contentProps.diffNavigation.totalAddedLines > 0 && (
                                                    <span className="repos-compare-added-lines font-weight-semibold">
                                                        <Tooltip
                                                            text={format(Resources.LinesAddedTooltip, contentProps.diffNavigation.totalAddedLines)}
                                                        >
                                                            <span>+{contentProps.diffNavigation.totalAddedLines}</span>
                                                        </Tooltip>
                                                    </span>
                                                )}
                                            </span>
                                            {!fileIsRoot && (
                                                <Tooltip overflowOnly={true} text={selectedItem.path}>
                                                    <span className="secondary-text body-s text-ellipsis">{selectedItem.path}</span>
                                                </Tooltip>
                                            )}
                                        </div>
                                        {
                                            !selectedItem.isFolder && headerItemProps.fileViewMode == ChangedFileViewMode.Diff && checkIfIndexingExtensionIsAvailable(selectedItem) && indexingButtonProperties?.displayToolTip &&
                                                <Button
                                                    style={{ background: 'None', paddingLeft: 'revert', color: indexingButtonProperties ? indexingButtonProperties?.buttonColor : NotInitializedButton.buttonColor }}
                                                    className="Indexing button"
                                                    ariaLabel={indexingButtonProperties ? indexingButtonProperties?.buttonText : NotInitializedButton.buttonText}
                                                    iconProps={{ iconName: indexingButtonProperties ? indexingButtonProperties?.buttonIcon : NotInitializedButton.buttonIcon }}
                                                    onClick={() => window.open(indexingButtonProperties ? indexingButtonProperties?.onClickRef : NotInitializedButton.onClickRef, '_blank')}
                                                />
                                        }
                                    </div>
                                    {!smallScreen && (
                                        <Observer fileContent={fileContent}>
                                            {() => (
                                                <FileViewModeButton
                                                    fileContent={fileContent?.value}
                                                    filesService={filesService}
                                                    headerItemProps={headerItemProps}
                                                    selectedItem={selectedItem}
                                                />
                                            )}
                                        </Observer>
                                    )}
                                    <HeaderCommandBar
                                        className="repos-compare-header-commandbar"
                                        items={[
                                            ...getHeaderItems(navigationService, headerItemProps, selectedItem, smallScreen),
                                            ...getExtraHeaderItems(selectedItem, viewService, discussionService, smallScreen)
                                        ]}
                                    />
                                </div>
                            );
                        }}
                    </Observer>
                )}
            </ScreenContext.Consumer>
        </div>
    );
}

interface IChangeCompareHeaderItemProps {
    fileViewMode: ChangedFileViewMode;
    pageContext: IVssPageContext;
    selectedItem: IChange;
    setFileViewMode: (viewMode: ChangedFileViewMode) => void;
}

interface IFileViewModeButtonProps<TVersionDescriptor> {
    fileContent?: IChangeItemContent;
    filesService: IChangesFilesService<TVersionDescriptor>;
    headerItemProps: IChangeCompareHeaderItemProps;
    selectedItem: IChange;
}

function FileViewModeButton<TVersionDescriptor>(props: IFileViewModeButtonProps<TVersionDescriptor>): React.ReactElement | null {
    const { fileContent, filesService, headerItemProps, selectedItem } = props;
    const viewerModeDropdownItems: IMenuItem[] = [];
    const isEdit = (selectedItem.changeType & VersionControlChangeType.Edit) !== 0 && (selectedItem.changeType & VersionControlChangeType.Add) === 0;
    const diffViewerType = headerItemProps.selectedItem.isFolder ? DiffViewerType.summary : DiffViewerType.detail;
    const orientation = filesService.getDiffOrientation(diffViewerType).value;

    const contentPreviewService = getContentPreviewService(headerItemProps.pageContext);
    const previewContribution = contentPreviewService.getPreviewContribution(selectedItem.path);
    const modifiedContentText = previewContribution ? Resources.RawContent : Resources.ModifiedContent;

    let toggleMenuItem: IMenuItem | undefined = undefined;
    let selectedMenuItem: IMenuItem | undefined = undefined;

    if (selectedItem.isFolder || isEdit) {
        const sideBySideItem = {
            iconProps: {
                iconName: "DiffSideBySide"
            },
            id: "diffOrientationSideBySide",
            onActivate: () => {
                if (headerItemProps.fileViewMode === ChangedFileViewMode.Diff && orientation == DiffViewerOrientation.SideBySide) {
                    return;
                }
                filesService.setDiffOrientation(diffViewerType, DiffViewerOrientation.SideBySide);
                if (headerItemProps.fileViewMode !== ChangedFileViewMode.Diff) {
                    headerItemProps.setFileViewMode(ChangedFileViewMode.Diff);
                }
            },
            text: Resources.SideBySideDiff
        };

        viewerModeDropdownItems.push(sideBySideItem);

        const inlineItem = {
            iconProps: {
                iconName: "DiffInline"
            },
            id: "diffOrientationInline",
            onActivate: () => {
                if (headerItemProps.fileViewMode === ChangedFileViewMode.Diff && orientation === DiffViewerOrientation.Inline) {
                    return;
                }
                filesService.setDiffOrientation(diffViewerType, DiffViewerOrientation.Inline);
                if (headerItemProps.fileViewMode !== ChangedFileViewMode.Diff) {
                    headerItemProps.setFileViewMode(ChangedFileViewMode.Diff);
                }
            },
            text: Resources.InlineDiff
        };

        viewerModeDropdownItems.push(inlineItem);

        toggleMenuItem = orientation === DiffViewerOrientation.SideBySide ? inlineItem : sideBySideItem;
        selectedMenuItem = orientation === DiffViewerOrientation.SideBySide ? sideBySideItem : inlineItem;
    }

    if (!selectedItem.isFolder) {
        const modifiedContentItem = {
            iconProps: {
                iconName: "TextDocument"
            },
            id: "modifiedContent",
            onActivate: () => {
                if (headerItemProps.fileViewMode === ChangedFileViewMode.ModifiedContent) {
                    return;
                }
                headerItemProps.setFileViewMode(ChangedFileViewMode.ModifiedContent);
            },
            text: modifiedContentText
        };

        viewerModeDropdownItems.push(modifiedContentItem);

        if (fileContent?.details.metadata.isImage) {
            const pixelDiffItem = {
                iconProps: {
                    iconName: "ImageDiff"
                },
                id: "image-pixel-diff",
                onActivate: () => {
                    if (headerItemProps.fileViewMode === ChangedFileViewMode.PixelDiff) {
                        return;
                    }
                    headerItemProps.setFileViewMode(ChangedFileViewMode.PixelDiff);
                },
                text: Resources.PixelDiff
            };

            if (headerItemProps.fileViewMode === ChangedFileViewMode.PixelDiff) {
                selectedMenuItem = pixelDiffItem;
            }

            viewerModeDropdownItems.push(pixelDiffItem);
        } else {
            const previewItem = {
                iconProps: {
                    iconName: "DocumentSearch"
                },
                id: "filePreview",
                onActivate: () => {
                    if (headerItemProps.fileViewMode === ChangedFileViewMode.Preview) {
                        return;
                    }
                    headerItemProps.setFileViewMode(ChangedFileViewMode.Preview);
                },
                text: Resources.Preview
            };

            if (headerItemProps.fileViewMode === ChangedFileViewMode.ModifiedContent || !toggleMenuItem) {
                selectedMenuItem = modifiedContentItem;

                if (previewContribution) {
                    toggleMenuItem = previewItem;
                }
            }

            if (previewContribution) {
                viewerModeDropdownItems.push(previewItem);

                if (headerItemProps.fileViewMode === ChangedFileViewMode.Preview) {
                    selectedMenuItem = previewItem;
                    toggleMenuItem = modifiedContentItem;
                }
            }
        }
    }

    return viewerModeDropdownItems.length > 1 && toggleMenuItem && selectedMenuItem ? (
        <SplitButton
            buttonProps={{
                onClick: toggleMenuItem.onActivate as () => void,
                text: selectedMenuItem.text,
                ariaLabel: selectedMenuItem.text,
                tooltipProps: {
                    text: format(Resources.FileViewModeTooltip, selectedMenuItem.text, toggleMenuItem.text)
                },
                iconProps: selectedMenuItem.iconProps
            }}
            className="flex-noshrink"
            menuButtonProps={{
                contextualMenuProps: {
                    menuProps: {
                        id: "changeViewerMode",
                        items: viewerModeDropdownItems
                    }
                }
            }}
            primary={false}
        />
    ) : null;
}

function getHeaderItems<TVersionDescriptor>(
    navigationService: IChangesNavigationService<TVersionDescriptor>,
    props: IChangeCompareHeaderItemProps,
    selectedItem: IChange,
    smallScreen?: boolean
): IMenuItem[] {
    const headerItems: IHeaderCommandBarItem[] = [];
    const isEdit = (selectedItem.changeType & VersionControlChangeType.Edit) !== 0;
    if (!smallScreen) {
        if (!selectedItem.isFolder) {
            headerItems.push({
                ariaLabel: Resources.PreviousDiff,
                disabled: !navigationService.getHasPreviousDiff(),
                iconProps: { iconName: "Up" },
                id: "prev-diff",
                important: true,
                onActivate: () => {
                    navigationService.previousDiff();
                },
                subtle: true,
                tooltipProps: { text: Resources.PreviousDiff }
            });
            headerItems.push({
                ariaLabel: Resources.NextDiff,
                disabled: !navigationService.getHasNextDiff(),
                iconProps: { iconName: "Down" },
                id: "next-diff",
                important: true,
                onActivate: () => {
                    navigationService.nextDiff();
                },
                subtle: true,
                tooltipProps: { text: Resources.NextDiff }
            });
        }

        if (!selectedItem.isFolder) {
            headerItems.push({
                ariaLabel: Resources.EditorPreferences,
                iconProps: { iconName: "Equalizer" },
                id: "view-options",
                important: true,
                onActivate: () => showPreferences(props.pageContext, props.fileViewMode === ChangedFileViewMode.Diff && isEdit),
                subtle: true,
                tooltipProps: { text: Resources.EditorPreferences }
            });
        }
    }

    return headerItems;
}

function showPreferences(pageContext: IVssPageContext, isDiffEditor: boolean) {
    // The preferences panel lives in a bundle that's not loaded initially, so let's ensure it's been already
    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
    contributionService.getContributionAsync("ms.vss-code-web.code-editor").then(() => {
        const preferencesService = pageContext.getService<IEditorUserPreferencesService>("IEditorUserPreferencesService");
        preferencesService.showPreferences(isDiffEditor);
    });
}

function getExtraHeaderItems(
    selectedItem: IChange,
    viewService?: IChangesViewService,
    discussionService?: IDiscussionIntegrationService,
    smallScreen?: boolean
): IHeaderCommandBarItem[] {
    const items: IHeaderCommandBarItem[] = [];
    if (viewService && discussionService) {
        (viewService.getCompareHeaderMenuItems(selectedItem, discussionService.getRootDiffSummaryIntegration(), smallScreen) || []).forEach(c => {
            items.push({ ...c, important: true, subtle: true });
        });
    }

    return items;
}
